import { Dispatch, SetStateAction, useState, useMemo } from 'react';
import { cn, debounce } from '~/util';
import { Input } from '~/primitives/input';
import { SearchIcon, X } from 'lucide-react';
import { ClassNameValue } from 'tailwind-merge';

interface SearchProps {
    initial_input: string;
    onInputChange: Dispatch<SetStateAction<string>> | ((value: string) => void);
    wrapperClassName?: ClassNameValue;
    inputClassName?: ClassNameValue;
}

function Search({
    initial_input,
    onInputChange,
    wrapperClassName,
    inputClassName,
}: SearchProps) {
    const [localInput, setLocalInput] = useState(initial_input);

    const debouncedSetInput = useMemo(
        () =>
            debounce((value: string) => {
                onInputChange(value);
            }, 250),
        [onInputChange],
    );

    const handleInputChange = (value: string) => {
        setLocalInput(value);
        debouncedSetInput(value);
    };

    return (
        <div
            className={cn(
                'flex items-center gap-4 bg-background-darker p-2 rounded-xl w-full max-w-xl',
                wrapperClassName,
            )}
        >
            <div className="flex items-center justify-center h-6 text-secondary-light">
                <SearchIcon className="w-6 h-6" />
            </div>

            <Input
                type="text"
                placeholder="Search"
                value={localInput}
                onChange={(event) => handleInputChange(event.target.value)}
                className={cn(
                    'w-full bg-transparent text-secondary-light font-medium outline-none focus:ring-0 p-2',
                    inputClassName,
                )}
            />

            {localInput !== '' && (
                <button
                    type="button"
                    aria-label="Clear search"
                    className="flex items-center justify-center h-6 w-6 rounded-md cursor-pointer transition-colors text-secondary-light hover:bg-background-dark hover:text-secondary-foreground"
                    onClick={() => {
                        onInputChange('');
                        setLocalInput('');
                    }}
                >
                    <X className="w-5 h-5" />
                </button>
            )}
        </div>
    );
}

export default Search;
